import ColumnBase from "./ColumnBase.jsx";
import {WIDGET_KEYS} from "../../other/constants/WidgetKeys.jsx";
import { ColumnStore, StringHelper } from "@bryntum/schedulerpro";
import {defaultGroupRenderer} from "../../other/utils/utils.jsx";
import { createTooltipRow, pluralize } from "../../other/utils/otherUtils.jsx";
import {
  ICON_COL_DEF_WIDTH,
  SWIVU_TOOLTIP_TRIGGER_CLS,
  TASK_BUDGET_FIELDS
} from "../../other/constants/OtherKeys.jsx";
import { GRANTS } from "../../other/constants/PrivilegeTypes.jsx";
import { userPreferences } from "../../react/context/bryntumContext.jsx";
import { PERSISTENCE_STATE_KEYS } from "../../other/constants/PersistenceStateKeys.jsx";
import { isColorBudgetCell } from "../../other/utils/budgetUtils.jsx";
import { BUDGET_FIELDS_CONFIG } from "../../other/constants/TaskBudget/taskBudgetViewColumnsData.js";

const DEFAULT_TOOLTIP_FIELDS = BUDGET_FIELDS_CONFIG.slice(0, 6).map(field => field.budgetField);
export const getBudgetIcon = (budgetRecord, applyColors = true) => {
  let color = "";
  if (budgetRecord) {
    // red when a budget number is negative
    // Bug: Since we don't have any column for gross profit/margin variance therefore its negative state is not refelcted in icon
    const isNegative = BUDGET_FIELDS_CONFIG.some((budgetFieldConfig) => budgetRecord[budgetFieldConfig.budgetField] < 0);
    color = isNegative ? "text-[#FF5722]" : "text-[#5ea1ed]";
  }
  return `<i class="fa-regular fa-coins relative left-0.5 ${applyColors ? color : ""}"></i>`;
}
export default class BudgetAccountColumn extends ColumnBase {
  static get $name() {
    return "BudgetAccountColumn";
  }

  static get type() {
    return WIDGET_KEYS.budgetAccountColumn;
  }

  static get defaults() {
    return {
      text: "Budget",
      field: "budgetId",
      editor: false,
      htmlEncode: false,
      width: 30,
      minWidth: ICON_COL_DEF_WIDTH,
      cellCls: "cursor-pointer",
      cls: "swivu-icon-column",
      sealed: true,
      filterable: false,
    };
  }

  headerRenderer() {
    let headerLabelText = "";
    const budgetLabelPref = userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.budgetLabel, "notDisplayed", true);
    if (budgetLabelPref && budgetLabelPref !== "notDisplayed") {
      const budgetFieldConfig = BUDGET_FIELDS_CONFIG.find(field => field.budgetField === budgetLabelPref);
      if (budgetFieldConfig) {
        headerLabelText = budgetFieldConfig.budgetFieldTitle;
      }
    }
    return `<div class="flex gap-[0.5em] items-center">
      ${getBudgetIcon()}
      <span class="text-wrap">${headerLabelText}</span>
    </div>`;
  }

  groupRenderer(e) {
    const { groupRowFor, record, ...rest } = e;
    const isHasBudget = typeof groupRowFor === "number";
    const plural = pluralize(rest.count, "Task", "s");
    const budgetRecord = this.project?.taskBudgetStore.getById(e.groupRecords?.[0]?.budgetId);
    const budgetText = isHasBudget
      ? `Budget #${
        budgetRecord?.[
          TASK_BUDGET_FIELDS.title
          ]
      }`
      : `No Budget ${plural}`;
    return defaultGroupRenderer({ groupRowFor: budgetText, ...rest });
  }

  calculateBudgetFormattingStyle(budgetFieldConfig, budgetValue) {
    let value = budgetValue, style = "";
    const { budgetValueFormatter, budgetField, budgetFieldTitle, isPercentage } = budgetFieldConfig;
    const canApplyStyle = isColorBudgetCell(budgetField);
    if (canApplyStyle) {
      style = `color:${budgetValue < 0 ? "#ff0000" : "#00b900"};`;
    }
    if (budgetValueFormatter) {
      value = budgetValueFormatter.format(value);
    } else if (isPercentage) {
      value = `${budgetValue}%`;
    }
    return { title: budgetFieldTitle, value, style };
  }

  tooltipRenderer({ record, tip }) {
    if (tip) {
      tip.minWidth = "25em";
      tip.cls = "swivu-task-tooltip swivu-budget-tooltip";
    }
    const budgetRecord = this.project?.taskBudgetStore.getById(record.budgetId);
    if (this.user.can(GRANTS.grant_two) && budgetRecord) {
      const budgetHoverOrder = StringHelper.safeJsonParse(userPreferences.getSetting(PERSISTENCE_STATE_KEYS.budgetHoverOrder, null, true)) || DEFAULT_TOOLTIP_FIELDS;
      const budgetHoverFields = budgetHoverOrder.reduce((prevFields, hoverField) => {
        const budgetFieldConfig = BUDGET_FIELDS_CONFIG.find(field => field.budgetField === hoverField);
        if (budgetFieldConfig) {
          const formattedBudget = this.calculateBudgetFormattingStyle(budgetFieldConfig, budgetRecord[budgetFieldConfig.budgetField]);
          prevFields.push(
            createTooltipRow(formattedBudget.value, "swivu-task-tooltip-row budget-row", formattedBudget.title, false, formattedBudget.style)
          )
        }
        return prevFields;
      }, []).join("");
      if (budgetHoverFields) {
        return `<div class="swivu-task-tooltip-grid flex flex-col p-1">${budgetHoverFields}</div>`;
      }
    }
  }

  onLoad(project, { user }) {
    this.user = user;
    this.project = project;
    if (userPreferences) {
      if (this.isBudgetLabelListener) {
        this.isBudgetLabelListener();
        this.isBudgetLabelListener = null;
      }
      this.isBudgetLabelListener = userPreferences.boardPreferencesStore.on("change", ({ record }) => {
        if (record?.key === PERSISTENCE_STATE_KEYS.budgetLabel) {
          this.refreshHeader();
        }
      }, this)
    }
  }

  renderer({ record }) {
    // Todo: Needs to be re-triggered if someone changes budget label field in budget record
    const budgetLabelPref = userPreferences?.getSetting(PERSISTENCE_STATE_KEYS.budgetLabel, "notDisplayed", true);
    const budgetFieldConfig = BUDGET_FIELDS_CONFIG.find(field => field.budgetField === budgetLabelPref);
    // Relations config sometimes return plain object instead of record instance
    // const budgetRecord = record.budgetRecord;
    const budgetRecord = this.project?.taskBudgetStore.getById(record.budgetId);
    let budgetValue = "", budgetValueStyle = "";
    if (budgetFieldConfig && budgetRecord?.[budgetFieldConfig.budgetField]) {
      const formattedBudget = this.calculateBudgetFormattingStyle(budgetFieldConfig, budgetRecord[budgetFieldConfig.budgetField]);
      budgetValue = formattedBudget.value;
      budgetValueStyle = formattedBudget.style;
    }
    return `<div class="${SWIVU_TOOLTIP_TRIGGER_CLS} flex gap-[0.5em] truncate">
      ${getBudgetIcon(budgetRecord)}
      <span style="${budgetValueStyle}">${budgetValue}</span>
    </div>`;
  }
}

ColumnStore.registerColumnType(BudgetAccountColumn);
